import { useState } from "react";
import { Lock, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function strengthOf(pwd) {
  if (!pwd) return { score: 0, label: "—" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const labels = ["Très faible", "Faible", "Moyen", "Bon", "Fort", "Très fort"];
  return { score, label: labels[Math.min(score, labels.length - 1)] };
}

export default function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const strength = strengthOf(next);
  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit = current && next && confirm && !mismatch && !tooShort && !sameAsCurrent && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setSuccess(true);
      toast.success("Mot de passe mis à jour ✅");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const barColors = ["bg-rose-500", "bg-rose-400", "bg-amber-500", "bg-amber-400", "bg-emerald-500", "bg-emerald-600"];

  const inputCls =
    "w-full px-3 py-2.5 text-sm rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-[#E11D48] focus:outline-none";

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-6 mb-8 shadow-sm"
      data-testid="change-password-card"
    >
      <div className="mb-5">
        <div className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold uppercase flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" /> // SECURITY
        </div>
        <h2 className="font-heading font-bold text-xl tracking-tight mt-1 text-slate-900">
          Changer mon mot de passe
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Pour votre sécurité, choisissez un mot de passe d'au moins 8 caractères.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
            Mot de passe actuel
          </label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="••••••••"
            data-testid="change-pwd-current"
            autoComplete="current-password"
            required
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
            Nouveau mot de passe
          </label>
          <div className="relative">
            <input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="Minimum 8 caractères"
              data-testid="change-pwd-new"
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              required
              className={`${inputCls} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowNext((v) => !v)}
              data-testid="change-pwd-toggle-visibility"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
            >
              {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {next && (
            <div className="mt-2">
              <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${barColors[strength.score] || "bg-rose-500"}`}
                  style={{ width: `${(strength.score / 5) * 100}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500 tracking-wider">
                <span>Robustesse : {strength.label}</span>
                <span>{next.length}/128</span>
              </div>
            </div>
          )}
          {tooShort && (
            <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Au moins 8 caractères requis
            </p>
          )}
          {sameAsCurrent && (
            <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Doit être différent de l'actuel
            </p>
          )}
        </div>

        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">
            Confirmer le nouveau mot de passe
          </label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Retapez le mot de passe"
            data-testid="change-pwd-confirm"
            autoComplete="new-password"
            required
            className={`${inputCls} ${mismatch ? "border-rose-500" : ""}`}
          />
          {mismatch && (
            <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Les mots de passe ne correspondent pas
            </p>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span data-testid="change-pwd-error">{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span data-testid="change-pwd-success">Mot de passe mis à jour avec succès.</span>
          </div>
        )}

        <div className="pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="change-pwd-submit"
            className="btn-primary text-xs uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Mise à jour…" : "Mettre à jour le mot de passe"}
          </button>
        </div>
      </form>
    </div>
  );
}
